import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Link } from 'react-router-dom';
import { Separator } from '@/components/ui/separator';
import { Chrome } from 'lucide-react';

interface AuthFormProps {
  type: 'signin' | 'signup' | 'reset-password';
}

export default function AuthForm({ type }: AuthFormProps) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { signIn, signUp, resetPassword, signInWithGoogle } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      switch (type) {
        case 'signin':
          await signIn(email, password);
          break;
        case 'signup':
          await signUp(email, password);
          break;
        case 'reset-password':
          await resetPassword(email);
          break;
      }
      navigate('/');
    } catch (error) {
      // Error is handled by the auth context
      console.error('Auth error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="w-full max-w-md mx-auto">
      <CardHeader>
        <CardTitle>
          {type === 'signin' && 'Sign In'}
          {type === 'signup' && 'Create Account'}
          {type === 'reset-password' && 'Reset Password'}
        </CardTitle>
        <CardDescription>
          {type === 'signin' && 'Enter your email and password to sign in'}
          {type === 'signup' && 'Create a new account to get started'}
          {type === 'reset-password' && 'Enter your email to reset your password'}
        </CardDescription>
      </CardHeader>
      <form onSubmit={handleSubmit}>
        <CardContent className="space-y-4">
          {type === 'signin' && (
            <>
              <Button
                type="button"
                className="w-full"
                onClick={() => signInWithGoogle()}
              >
                <Chrome className="mr-2 h-4 w-4" />
                Continue with Google
              </Button>
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <Separator className="w-full" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-background px-2 text-muted-foreground">
                    Or continue with
                  </span>
                </div>
              </div>
            </>
          )}
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          {type !== 'reset-password' && (
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
          )}
        </CardContent>
        <CardFooter className="flex flex-col space-y-4">
          <Button type="submit" className="w-full" disabled={loading}>
            {loading ? (
              <div className="flex items-center space-x-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span>Loading...</span>
              </div>
            ) : (
              type === 'signin' && 'Sign In' ||
              type === 'signup' && 'Create Account' ||
              type === 'reset-password' && 'Send Reset Link'
            )}
          </Button>
          <div className="text-sm text-center space-y-2">
            {type === 'signin' && (
              <>
                <p>
                  Don't have an account?{' '}
                  <Link to="/auth/signup" className="text-primary hover:underline">
                    Sign up
                  </Link>
                </p>
                <p>
                  <Link to="/auth/reset-password" className="text-primary hover:underline">
                    Forgot your password?
                  </Link>
                </p>
              </>
            )}
            {type === 'signup' && (
              <p>
                Already have an account?{' '}
                <Link to="/auth/signin" className="text-primary hover:underline">
                  Sign in
                </Link>
              </p>
            )}
            {type === 'reset-password' && (
              <p>
                Remember your password?{' '}
                <Link to="/auth/signin" className="text-primary hover:underline">
                  Sign in
                </Link>
              </p>
            )}
          </div>
        </CardFooter>
      </form>
    </Card>
  );
} 